import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { useDeploymentInfo } from '../hooks/useDeploymentInfo';
import { Collection, TokenMetadata, COLLECTION_ABI, CLOSED_COLLECTION_ABI } from '../types/contracts';
import { makeContractCall, randomDelay, checkContractExists, makeSequentialContractCalls } from '../utils/contractUtils';

interface CollectionManagerProps {
  collections: Collection[];
}

const CollectionManager: React.FC<CollectionManagerProps> = ({ collections }) => {
  const [selectedCollection, setSelectedCollection] = useState('');
  const [baseURI, setBaseURI] = useState('');
  const [maxSupply, setMaxSupply] = useState('');
  const [contractLocked, setContractLocked] = useState(false);
  const [lockTimestamp, setLockTimestamp] = useState(0);
  const [totalSupply, setTotalSupply] = useState(0);
  const [minterAddress, setMinterAddress] = useState('');
  const [minterAllowed, setMinterAllowed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [status, setStatus] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
  } | null>(null);
  const [tokenMetadata, setTokenMetadata] = useState<TokenMetadata[]>([]);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);

  const { deploymentInfo } = useDeploymentInfo();

  const selectedCollectionData = collections.find(c => c.address === selectedCollection);

  // Load collection data when selection changes
  useEffect(() => {
    if (selectedCollection) {
      loadCollectionData();
    }
  }, [selectedCollection]);

  // Auto-load metadata when collection is selected
  useEffect(() => {
    if (selectedCollection) {
      loadTokenMetadata();
    }
  }, [selectedCollection]);

  const loadCollectionData = async () => {
    if (!selectedCollection) return;

    setIsLoading(true);
    try {
      if (!window.ethereum) {
        throw new Error('MetaMask is not installed');
      }

      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();

      const contract = new ethers.Contract(selectedCollection, COLLECTION_ABI, provider);
      
      // Make calls sequentially with random delays to avoid rate limiting
      console.log('Loading collection data for:', selectedCollection);
      
      const [
        baseURIValue,
        totalSupplyValue,
        contractLockedValue,
        lockTimestampValue,
        ownerValue
      ] = await makeSequentialContractCalls(
        [
          () => contract.baseURI(),
          () => contract.totalSupply(),
          () => contract.contractLocked(),
          () => contract.lockTimestamp(),
          () => contract.owner()
        ],
        [
          'get base URI',
          'get total supply',
          'get contract locked status',
          'get lock timestamp',
          'get owner'
        ]
      );

      setBaseURI(baseURIValue);
      setContractLocked(contractLockedValue);
      setLockTimestamp(Number(lockTimestampValue));
      setTotalSupply(Number(totalSupplyValue));

      // For closed collections, also load max supply
      if (selectedCollectionData?.type === 'closed') {
        await randomDelay(300, 600);
        const closedContract = new ethers.Contract(selectedCollection, CLOSED_COLLECTION_ABI, provider);
        const maxSupplyValue = await makeContractCall(
          () => closedContract.maxSupply(),
          'get max supply'
        );
        setMaxSupply(maxSupplyValue.toString());
      }

      // Check if current user is owner
      const currentAddress = await signer.getAddress();
      if (currentAddress.toLowerCase() !== ownerValue.toLowerCase()) {
        setStatus({
          type: 'error',
          message: 'You are not the owner of this collection'
        });
      }

    } catch (error) {
      console.error('Error loading collection data:', error);
      setStatus({
        type: 'error',
        message: `Failed to load collection data: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateBaseURI = async () => {
    if (!selectedCollection || !baseURI.trim()) {
      setStatus({
        type: 'error',
        message: 'Please select a collection and enter a base URI'
      });
      return;
    }

    setIsUpdating(true);
    try {
      if (!window.ethereum) {
        throw new Error('MetaMask is not installed');
      }

      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();

      const collectionABI = [
        "function setBaseURI(string memory _baseURI) public"
      ];

      const contract = new ethers.Contract(selectedCollection, collectionABI, signer);
      const tx = await contract.setBaseURI(baseURI);
      await tx.wait();

      setStatus({
        type: 'success',
        message: 'Base URI updated successfully'
      });

    } catch (error) {
      console.error('Error updating base URI:', error);
      setStatus({
        type: 'error',
        message: `Failed to update base URI: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const updateMaxSupply = async () => {
    if (!selectedCollection || !maxSupply.trim() || selectedCollectionData?.type !== 'closed') {
      setStatus({
        type: 'error',
        message: 'Please select a closed collection and enter a max supply'
      });
      return;
    }

    setIsUpdating(true);
    try {
      if (!window.ethereum) {
        throw new Error('MetaMask is not installed');
      }

      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();

      const collectionABI = [
        "function setMaxSupply(uint256 _maxSupply) public"
      ];

      const contract = new ethers.Contract(selectedCollection, collectionABI, signer);
      const tx = await contract.setMaxSupply(maxSupply);
      await tx.wait();

      setStatus({
        type: 'success',
        message: 'Max supply updated successfully'
      });

    } catch (error) {
      console.error('Error updating max supply:', error);
      setStatus({
        type: 'error',
        message: `Failed to update max supply: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const lockContract = async () => {
    if (!selectedCollection) {
      setStatus({
        type: 'error',
        message: 'Please select a collection'
      });
      return;
    }

    setIsUpdating(true);
    try {
      if (!window.ethereum) {
        throw new Error('MetaMask is not installed');
      }

      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();

      const collectionABI = [
        "function lockContract() public"
      ];

      const contract = new ethers.Contract(selectedCollection, collectionABI, signer);
      const tx = await contract.lockContract();
      await tx.wait();

      setStatus({
        type: 'success',
        message: 'Contract locked successfully'
      });

      // Reload collection data to update state
      loadCollectionData();

    } catch (error) {
      console.error('Error locking contract:', error);
      setStatus({
        type: 'error',
        message: `Failed to lock contract: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const revertLock = async () => {
    if (!selectedCollection) {
      setStatus({
        type: 'error',
        message: 'Please select a collection'
      });
      return;
    }

    setIsUpdating(true);
    try {
      if (!window.ethereum) {
        throw new Error('MetaMask is not installed');
      }

      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();

      const collectionABI = [
        "function revertLock() public"
      ];

      const contract = new ethers.Contract(selectedCollection, collectionABI, signer);
      const tx = await contract.revertLock();
      await tx.wait();

      setStatus({
        type: 'success',
        message: 'Contract lock reverted successfully'
      });

      // Reload collection data to update state
      loadCollectionData();

    } catch (error) {
      console.error('Error reverting lock:', error);
      setStatus({
        type: 'error',
        message: `Failed to revert lock: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const manageMinter = async () => {
    if (!selectedCollection || !minterAddress.trim()) {
      setStatus({
        type: 'error',
        message: 'Please select a collection and enter a minter address'
      });
      return;
    }

    if (!ethers.isAddress(minterAddress)) {
      setStatus({
        type: 'error',
        message: 'Please enter a valid minter address'
      });
      return;
    }

    setIsUpdating(true);
    try {
      if (!window.ethereum) {
        throw new Error('MetaMask is not installed');
      }

      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();

      const collectionABI = [
        "function manageMinter(address _minter, bool _allowed) public"
      ];

      const contract = new ethers.Contract(selectedCollection, collectionABI, signer);
      const tx = await contract.manageMinter(minterAddress, minterAllowed);
      await tx.wait();

      setStatus({
        type: 'success',
        message: `Minter ${minterAddress} ${minterAllowed ? 'allowed' : 'disallowed'} successfully`
      });

      // Clear form
      setMinterAddress('');
      setMinterAllowed(false);

    } catch (error) {
      console.error('Error managing minter:', error);
      setStatus({
        type: 'error',
        message: `Failed to manage minter: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const loadTokenMetadata = async () => {
    if (!selectedCollection) return;

    setIsLoadingMetadata(true);
    setTokenMetadata([]);

    try {
      if (!window.ethereum) {
        throw new Error('MetaMask is not installed');
      }

      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();

      const collectionABI = [
        "function totalSupply() public view returns (uint256)",
        "function tokenURI(uint256 _tokenId) public view returns (string)",
        "function ownerOf(uint256 _tokenId) public view returns (address)"
      ];

      const contract = new ethers.Contract(selectedCollection, collectionABI, provider);
      const totalSupply = await makeContractCall(
        () => contract.totalSupply(),
        'get total supply for metadata'
      );

      const results: TokenMetadata[] = [];

      // Check tokens up to 10 to find existing tokens
      const maxTokenIdToCheck = 10;

      console.log(`Checking tokens up to ${maxTokenIdToCheck} to find ${totalSupply} minted tokens...`);
      
      // First, find which tokens actually exist by checking ownerOf
      const existingTokens: number[] = [];
      for (let tokenId = 1; tokenId <= maxTokenIdToCheck && existingTokens.length < Number(totalSupply); tokenId++) {
        try {
          // Try to get owner - if token exists, this will succeed
          // Use maxRetries=2 since a revert is expected for non-existent tokens
          await makeContractCall(
            () => contract.ownerOf(tokenId),
            `check if token ${tokenId} exists`,
            2, // Only retry 2 times for token existence checks
            500 // Shorter base delay
          );
          existingTokens.push(tokenId);
          console.log(`Found token ${tokenId}`);
        } catch (err) {
          // Token doesn't exist, skip it
          continue;
        }
        
        // Add small delay to avoid rate limiting
        if (tokenId < maxTokenIdToCheck) {
          await randomDelay(100, 300);
        }
      }

      console.log(`Found ${existingTokens.length} tokens: ${existingTokens.join(', ')}`);

      // Now fetch metadata for existing tokens
      for (let i = 0; i < existingTokens.length; i++) {
        const tokenId = existingTokens[i];
        try {
          const tokenURI = await makeContractCall(
            () => contract.tokenURI(tokenId),
            `get token URI for token ${tokenId}`
          );
          
          // Try to fetch metadata
          let metadata = null;
          let error = null;

          try {
            const response = await fetch(tokenURI);
            if (response.ok) {
              metadata = await response.json();
            } else {
              error = `HTTP ${response.status}: ${response.statusText}`;
            }
          } catch (fetchError) {
            error = `Failed to fetch: ${fetchError instanceof Error ? fetchError.message : 'Unknown error'}`;
          }

          results.push({
            tokenId,
            tokenURI,
            metadata,
            error
          });
          
          // Add random delay between token calls
          if (i < existingTokens.length - 1) {
            await randomDelay(300, 800);
          }
        } catch (err) {
          results.push({
            tokenId,
            tokenURI: '',
            metadata: null,
            error: `Failed to get tokenURI: ${err instanceof Error ? err.message : 'Unknown error'}`
          });
        }
      }

      setTokenMetadata(results);

    } catch (error) {
      console.error('Error loading token metadata:', error);
      setStatus({
        type: 'error',
        message: `Failed to load token metadata: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsLoadingMetadata(false);
    }
  };

  const openTokenURI = (uri: string) => {
    window.open(uri, '_blank');
  };

  if (collections.length === 0) {
    return (
      <div className="contract-section">
        <h2>Collection Manager</h2>
        <div className="status info">
          No collections available. Please deploy a collection first.
        </div>
      </div>
    );
  }

  return (
    <div className="contract-section">
      <h2>Collection Manager</h2>
      <p>Manage your collection settings and view token metadata.</p>

      <div className="form-group">
        <label>Select Collection *</label>
        <select 
          value={selectedCollection} 
          onChange={(e) => setSelectedCollection(e.target.value)}
        >
          <option value="">Choose a collection...</option>
          {collections.map((collection) => (
            <option key={collection.address} value={collection.address}>
              {collection.name} ({collection.type}) - {collection.address.slice(0, 10)}...
            </option>
          ))}
        </select>
      </div>

      {selectedCollectionData && (
        <div className="contract-card">
          <h3>Selected Collection</h3>
          <p><strong>Name:</strong> {selectedCollectionData.name}</p>
          <p><strong>Type:</strong> {selectedCollectionData.type}</p>
          <p><strong>Total Supply:</strong> {totalSupply} NFTs minted</p>
          {selectedCollectionData.type === 'closed' && maxSupply && (
            <p><strong>Max Supply:</strong> {maxSupply} NFTs</p>
          )}
          <p><strong>Address:</strong> <span className="contract-address">{selectedCollectionData.address}</span></p>
        </div>
      )}

      {selectedCollection && (
        <>
          <div className="form-group">
            <label>Base URI</label>
            <input
              type="text"
              value={baseURI}
              onChange={(e) => setBaseURI(e.target.value)}
              placeholder="https://api.example.com/metadata/"
              disabled={isLoading}
            />
            <small>The base URI for token metadata (should end with /)</small>
          </div>

          <button 
            className="btn btn-primary" 
            onClick={updateBaseURI}
            disabled={isUpdating || isLoading}
          >
            {isUpdating ? (
              <>
                <span className="loading"></span>
                Updating...
              </>
            ) : (
              'Update Base URI'
            )}
          </button>

          {selectedCollectionData?.type === 'closed' && (
            <>
              <div className="form-group">
                <label>Max Supply</label>
                <input
                  type="number"
                  value={maxSupply}
                  onChange={(e) => setMaxSupply(e.target.value)}
                  placeholder="10000"
                  min="1"
                  disabled={isLoading}
                />
                <small>Maximum number of tokens that can be minted</small>
              </div>

              <button 
                className="btn btn-primary" 
                onClick={updateMaxSupply}
                disabled={isUpdating || isLoading}
              >
                {isUpdating ? (
                  <>
                    <span className="loading"></span>
                    Updating...
                  </>
                ) : (
                  'Update Max Supply'
                )}
              </button>
            </>
          )}

          {selectedCollection && (
            <>
              <div className="form-group">
                <label>Contract Lock Status</label>
                <div className="contract-status">
                  <span className={`status ${contractLocked ? 'locked' : 'unlocked'}`}>
                    {contractLocked ? '🔒 LOCKED' : '🔓 UNLOCKED'}
                  </span>
                  {contractLocked && lockTimestamp > 0 && (
                    <small>
                      Locked at: {new Date(lockTimestamp * 1000).toLocaleString()}
                      <br />
                      Grace period expires: {new Date((lockTimestamp + 24 * 60 * 60) * 1000).toLocaleString()}
                    </small>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Contract Lock Controls</label>
                <div className="button-group">
                  <button 
                    className="btn btn-warning" 
                    onClick={lockContract}
                    disabled={isUpdating || contractLocked}
                  >
                    {isUpdating ? (
                      <>
                        <span className="loading"></span>
                        Locking...
                      </>
                    ) : (
                      'Lock Contract'
                    )}
                  </button>
                  
                  <button 
                    className="btn btn-success" 
                    onClick={revertLock}
                    disabled={isUpdating || !contractLocked}
                  >
                    {isUpdating ? (
                      <>
                        <span className="loading"></span>
                        Reverting...
                      </>
                    ) : (
                      'Revert Lock'
                    )}
                  </button>
                </div>
                <small>
                  Locking prevents further changes. Revert lock is only possible within 24 hours.
                </small>
              </div>

              <div className="form-group">
                <label>Manage Minters</label>
                <div className="input-group">
                  <input
                    type="text"
                    value={minterAddress}
                    onChange={(e) => setMinterAddress(e.target.value)}
                    placeholder="0x..."
                    className={minterAddress && !ethers.isAddress(minterAddress) ? 'error' : ''}
                  />
                  <select 
                    value={minterAllowed ? 'true' : 'false'} 
                    onChange={(e) => setMinterAllowed(e.target.value === 'true')}
                  >
                    <option value="true">Allow</option>
                    <option value="false">Disallow</option>
                  </select>
                  <button 
                    className="btn btn-primary" 
                    onClick={manageMinter}
                    disabled={isUpdating || !minterAddress.trim()}
                  >
                    {isUpdating ? (
                      <>
                        <span className="loading"></span>
                        Updating...
                      </>
                    ) : (
                      'Update Minter'
                    )}
                  </button>
                </div>
                <small>Manage which addresses can mint tokens from this collection</small>
              </div>
            </>
          )}

          {tokenMetadata.length > 0 && (
            <div className="metadata-section">
              <h3>Token Metadata</h3>
              <div className="metadata-grid">
                {tokenMetadata.map((token) => (
                  <div key={token.tokenId} className="metadata-card">
                    <h4>Token #{token.tokenId}</h4>
                    <div className="token-uri">
                      <strong>Token URI:</strong>
                      <div className="uri-container">
                        <span className="uri-text">{token.tokenURI}</span>
                        {token.tokenURI && (
                          <button 
                            className="btn btn-small"
                            onClick={() => openTokenURI(token.tokenURI)}
                          >
                            Open
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {token.error ? (
                      <div className="metadata-error">
                        <strong>Error:</strong> {token.error}
                      </div>
                    ) : token.metadata ? (
                      <div className="metadata-content">
                        <pre>{JSON.stringify(token.metadata, null, 2)}</pre>
                      </div>
                    ) : (
                      <div className="metadata-loading">Loading metadata...</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedCollection && (
            <div className="form-group" style={{ marginTop: '10px' }}>
              <button 
                className="btn btn-secondary" 
                onClick={loadTokenMetadata}
                disabled={isLoadingMetadata}
              >
                {isLoadingMetadata ? (
                  <>
                    <span className="loading"></span>
                    Loading Metadata...
                  </>
                ) : (
                  'Reload Token Metadata'
                )}
              </button>
            </div>
          )}
        </>
      )}

      {status && (
        <div className={`${status.type === 'success' ? 'success-message' : 
                        status.type === 'error' ? 'error-message' : 
                        'status info'}`}>
          {status.message}
        </div>
      )}
    </div>
  );
};

export default CollectionManager;
